import React from 'react';
import { AuthProvider, useAuth, ROLES } from './AuthContext';
import Login from './Login';
import Dashboard from './Dashboard';
import ResetPassword from './ResetPassword';
import BlockedScreen from './components/BlockedScreen';
import { redirectToBilling, rememberBillingHost } from './utils/auth';
import './App.css';
import './services/errorLogger';

function AppContent() {
  const { user, role, canAccessDashboard, signOut, passwordRecovery } = useAuth();

  // A password-recovery landing takes priority over every other route — the
  // recovery link creates a live session, so without this the app would fall
  // straight through to the dashboard and never show the set-password form.
  if (passwordRecovery) {
    return <ResetPassword />;
  }

  // Handle billing redirect with token
  if (user && canAccessDashboard) {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('redirect') === 'billing') {
      if (role) {
        // __STICKY_REDIRECT__ consume the pending key here too. This branch acts on
        // the URL param and used to leave the sessionStorage copy behind, which is
        // how a one-off bounce turned into a permanent one for the rest of the tab.
        sessionStorage.removeItem('postLoginRedirect');
        redirectToBilling(role);
      }
      // Role not loaded yet — render nothing and wait for re-render
      return null;
    }
  }

  // Handle logout action from billing
  const logoutParams = new URLSearchParams(window.location.search);
  if (logoutParams.get('action') === 'logout') {
    signOut().then(() => {
      window.history.replaceState({}, document.title, '/');
    });
    return null;
  }

  // If not logged in, show Login
  if (!user) {
    // __RETURN_HOST__ note which billing name sent us BEFORE signing in -- a
    // Google sign-in comes back without the query string. Allow-listed inside.
    rememberBillingHost();
    return <Login />;
  }

  // Drivers are blocked from the board (6932308) and use the HMGC Driver app.
  // This is also where a driver lands straight after setting a password from an
  // invite link (updatePassword clears passwordRecovery), so the screen says
  // "Password set -- open the HMGC Driver app" with the download link instead
  // of the blocked text, which other roles on this screen still get.
  if (role === ROLES.DRIVER) {
    return <BlockedScreen role={role} onLogout={signOut} />;
  }

  // __SHOP_ROLE__ Shop / Fleet logins use HM Shop Desktop and the Shop app, not this board: the
  // screen says "Your account is for HM Shop only" with the HM Shop Desktop link.
  if (role === ROLES.SHOP) {
    return <BlockedScreen role={role} onLogout={signOut} />;
  }

  // If user is owner or dispatcher, show Dashboard
  if (canAccessDashboard) {
    const pendingRedirect = sessionStorage.getItem('postLoginRedirect');
    if (pendingRedirect === 'billing') {
      sessionStorage.removeItem('postLoginRedirect');
      redirectToBilling(role);
      return null;
    }
    return <Dashboard />;
  }

  // Fallback: show Dashboard (for users without a role yet)
  return <Dashboard />;
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
